import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller, useWatch } from "react-hook-form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import Select from "react-select";

import { useDarkMode } from "../../../../context/ThemeContext";
import BackButton from "../../components/BackButton";
import SgTable from "../../components/SgTable";
import type { SgColumn } from "../../components/SgTable";
import { StatusBadge, ConnCell, OriginChips } from "../../components/cells";
import {
  useSgOrigins,
  originsOfImei,
} from "../../../../features/sg/origins";
import { downloadXlsx } from "../../../../features/sg/xlsx";
import { userDeviceRow, fileDate } from "../../../../features/sg/exportRows";
import {
  fetchUsuarioLookups,
  fetchUsuario,
  saveUsuario,
  deleteUsuario,
} from "../../../../features/sg/usuarios";
import {
  ORIGIN_OPTIONS,
  emptyUsuarioForm,
  toUsuarioForm,
} from "../../../../features/sg/schemas";
import { notifyOk, notifyError, notifyLoading, confirmAction } from "../../../../features/sg/notify";
import {
  fetchV5CompanyState,
  setV5CompanyOperation,
} from "../../../../features/sg/v5Company";
import { archiveOrAlert } from "../../../../features/device-archive/jobs";
import { pickBackupSources } from "../../../../features/device-archive/backupModal";
import { timeAgo, connStatus } from "../../../../features/sg/format";
import { useSgDeviceConnections } from "../../../../features/sg/useSgDeviceConnections";
import AccountContacts from "./AccountContacts";

import {
  FormContainer,
  Path,
  ButtonsContainer,
  Form,
  SectionsContainer,
  Section,
  SectionTitle,
  SectionContainer,
  InputContainer,
  InputLabel,
  Input,
  Error as ErrorLabel,
  Button,
} from "./styles";
import { getCustomSelectStyles } from "../../../../Layouts/admin/shared/theme/selectStyles";

const ADDRESS_FIELDS = [
  { name: "business_name", labelKey: "businessName", full: true },
  { name: "first_address", labelKey: "address1" },
  { name: "second_address", labelKey: "address2" },
  { name: "city", labelKey: "city" },
  { name: "state", labelKey: "state" },
  { name: "zip_code", labelKey: "zip" },
  { name: "country", labelKey: "country" },
  { name: "phone", labelKey: "phone" },
  { name: "email", labelKey: "email" },
];

export default function UsersForm() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation("sg");
  const { isDarkMode } = useDarkMode();

  const inputClass = isDarkMode
    ? "bg-[#232323] text-white border-dark-border"
    : "bg-white text-gray-900 border-[#DFE5EF]";
  const labelClass = isDarkMode ? "text-white" : "text-gray-500";
  const sectionSpan = isDarkMode
    ? "px-2 bg-[#232323] text-white"
    : "px-2 bg-white text-[#757575]";

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({ defaultValues: emptyUsuarioForm() });

  const watchedUserStatusId = useWatch({ control, name: "user_status_id" });
  const watchedOrigin = useWatch({ control, name: "origin" });
  const showV5Access =
    isNew && Array.isArray(watchedOrigin) && watchedOrigin.includes("v5");

  const { data: lookups } = useQuery({
    queryKey: ["sg-usuario-lookups"],
    queryFn: fetchUsuarioLookups,
  });

  const statusOptions = (lookups?.statuses ?? [])
    .filter((s: any) =>
      ["facturable", "no facturable"].includes((s.name || "").toLowerCase())
    )
    .map((s: any) => ({ value: s.id, label: s.name }));
  const conditionOptions = (lookups?.conditions ?? [])
    .filter(
      (c: any) => String(c.user_status_id) === String(watchedUserStatusId)
    )
    .map((c: any) => ({ value: c.id, label: c.name }));

  const { data: userData } = useQuery({
    queryKey: ["sg-user-edit", id],
    queryFn: () => fetchUsuario(id!),
    enabled: !isNew,
  });

  const associatedDevices = userData?.devices ?? [];
  const deviceImeis = useMemo(
    () => associatedDevices.map((d: any) => d.imei).filter(Boolean),
    [associatedDevices]
  );
  const livePositions = useSgDeviceConnections(deviceImeis);
  const deviceOrigins = useSgOrigins(deviceImeis);

  // .xlsx de las unidades del usuario con la plataforma de cada una.
  function exportDevices() {
    const rows = associatedDevices.map((d: any) =>
      userDeviceRow(
        d,
        livePositions[d.imei]?.timestamp || d.last_time_connected,
        deviceOrigins
      )
    );
    return downloadXlsx(
      `unidades_${userData?.user?.user_name || id}_${fileDate()}.xlsx`,
      "Unidades",
      [
        t("userForm.devicesCols.name"),
        t("userForm.devicesCols.uniqId"),
        t("barrido.cols.imei"),
        t("userForm.devicesCols.status"),
        t("userForm.devicesCols.model"),
        t("userForm.devicesCols.lastConn"),
        t("userForm.devicesCols.phone"),
        t("barrido.cols.platforms"),
      ],
      rows
    ).catch((err: any) => notifyError(err.message));
  }

  useEffect(() => {
    if (userData) reset(toUsuarioForm(userData) as any);
  }, [userData, reset]);

  useEffect(() => {
    const current = getValues("account_condition_id");
    if (!current) return;
    const cond = (lookups?.conditions ?? []).find(
      (c: any) => String(c.id) === String(current)
    );
    if (cond && String(cond.user_status_id) !== String(watchedUserStatusId)) {
      setValue("account_condition_id", "");
    }
  }, [watchedUserStatusId, lookups, getValues, setValue]);

  const saveMutation = useMutation({
    mutationFn: (values: any) =>
      saveUsuario({
        id,
        isNew,
        values,
        billingStatusChanged:
          statusNameById(values.user_status_id) !==
          statusNameById(userData?.user?.user_status_id),
      }),
    onMutate: () => {
      notifyLoading();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sg-users"] });
      queryClient.invalidateQueries({ queryKey: ["sg-counts"] });
      if (!isNew) queryClient.invalidateQueries({ queryKey: ["sg-user-edit", id] });
      notifyOk();
      navigate("/sg/users", { replace: true });
    },
    onError: (err: any) => notifyError(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteUsuario(id!),
    onMutate: () => {
      notifyLoading(t("userForm.deleting"));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sg-users"] });
      queryClient.invalidateQueries({ queryKey: ["sg-counts"] });
      notifyOk(t("userForm.deleted"));
      navigate("/sg/users", { replace: true });
    },
    onError: (err: any) => notifyError(err.message),
  });

  const statusNameById = (statusId: any) =>
    (
      (lookups?.statuses ?? []).find(
        (s: any) => String(s.id) === String(statusId)
      )?.name ?? ""
    ).toLowerCase();

  const v5CompanyId = () => userData?.user?.origin_ids?.v5 as string | undefined;

  const linkedCompanyId = v5CompanyId();
  const savedStatusIsNotBillable =
    statusNameById(userData?.user?.user_status_id) === "no facturable";

  const { data: v5Company, isLoading: v5CompanyLoading } = useQuery({
    queryKey: ["sg-v5-company", linkedCompanyId],
    queryFn: () => fetchV5CompanyState(linkedCompanyId!),
    enabled: !isNew && !!linkedCompanyId,
  });

  const v5OperationMutation = useMutation({
    mutationFn: (suspend: boolean) =>
      setV5CompanyOperation(linkedCompanyId!, suspend),
    onMutate: (suspend) => {
      notifyLoading(
        suspend
          ? t("userForm.v5Operation.suspending")
          : t("userForm.v5Operation.activating")
      );
    },
    onSuccess: (result, suspend) => {
      queryClient.invalidateQueries({
        queryKey: ["sg-v5-company", linkedCompanyId],
      });
      const doneKey = suspend
        ? "userForm.v5Operation.suspendedDone"
        : "userForm.v5Operation.activatedDone";
      notifyOk(
        result.devicesUpdated === null
          ? t(doneKey)
          : t(`${doneKey}Count`, { count: result.devicesUpdated })
      );
    },
    onError: (err: any) => notifyError(err.message),
  });

  async function onToggleV5Operation() {
    if (!v5Company || !linkedCompanyId) return;

    if (!v5Company.suspended) {
      const choice = await pickBackupSources("suspend", { v5Note: true });
      if (choice.action === "cancel") return;
      if (choice.action === "archive") {
        await archiveOrAlert(linkedCompanyId, "suspend", choice.sources);
      }
      v5OperationMutation.mutate(true);
      return;
    }

    const ok = await confirmAction({
      title: t("userForm.v5Operation.confirmActivateTitle"),
      text: t("userForm.v5Operation.confirmActivateText"),
      confirmText: t("userForm.v5Operation.activate"),
    });
    if (!ok) return;
    v5OperationMutation.mutate(false);
  }

  async function onSubmitForm(values: any) {
    const companyId = v5CompanyId();
    const linkedV5 =
      Array.isArray(values.origin) && values.origin.includes("v5") && !!companyId;
    const becomingNoFact =
      statusNameById(values.user_status_id) === "no facturable" &&
      statusNameById(userData?.user?.user_status_id) !== "no facturable";

    if (!isNew && linkedV5 && becomingNoFact) {
      const choice = await pickBackupSources("suspend", { v5Note: true });
      if (choice.action === "cancel") return;
      if (choice.action === "archive") {
        await archiveOrAlert(companyId!, "suspend", choice.sources);
      }
    }
    saveMutation.mutate(values);
  }

  async function onDelete() {
    const linkedV5 = ((getValues("origin") as string[]) ?? []).includes("v5");
    const companyId = v5CompanyId();

    if (linkedV5 && companyId) {
      const choice = await pickBackupSources("delete", { v5Note: true });
      if (choice.action === "cancel") return;
      if (choice.action === "archive") {
        await archiveOrAlert(companyId, "delete", choice.sources);
      }
      deleteMutation.mutate();
      return;
    }

    const ok = await confirmAction({
      title: t("userForm.deleteTitle"),
      text: t("common.confirmText"),
      confirmText: t("userForm.deleteConfirm"),
    });
    if (ok) deleteMutation.mutate();
  }

  const saving = saveMutation.isPending;
  const deleting = deleteMutation.isPending;

  const deviceCols: SgColumn[] = [
    {
      header: t("userForm.devicesCols.name"),
      key: "name",
      render: (d) => <span>{d.name || "—"}</span>,
    },
    {
      header: t("userForm.devicesCols.uniqId"),
      key: "uniq_id",
      render: (d) => <span>{d.uniq_id || "—"}</span>,
    },
    {
      header: t("userForm.devicesCols.status"),
      key: "status",
      render: (d) => (
        <StatusBadge
          label={d.statuses?.name_es || d.statuses?.name || "—"}
          isDarkMode={isDarkMode}
        />
      ),
    },
    {
      header: t("userForm.devicesCols.model"),
      key: "model",
      render: (d) => <span>{d.brands?.model || "—"}</span>,
    },
    {
      header: t("barrido.cols.platforms"),
      key: "platforms",
      render: (d) => <OriginChips origins={originsOfImei(deviceOrigins, d.imei)} />,
    },
    {
      header: t("userForm.devicesCols.lastConn"),
      key: "lastConn",
      render: (d) => {
        const live = livePositions[d.imei];
        const lc = live?.timestamp || d.last_time_connected;
        return lc ? (
          <ConnCell status={connStatus(lc)} label={timeAgo(lc)} live={!!live} />
        ) : (
          <ConnCell status={null} label={t("devices.noData")} />
        );
      },
    },
    {
      header: t("userForm.devicesCols.phone"),
      key: "phone",
      render: (d) => <span>{d.phone || "—"}</span>,
    },
  ];

  return (
    <FormContainer>
      <Path className="text-gray-400">
        {isNew ? t("userForm.breadcrumbNew") : t("userForm.breadcrumbEdit")}
      </Path>
      <Form onSubmit={handleSubmit(onSubmitForm)}>
        <SectionsContainer className={isDarkMode ? "bg-[#232323]" : "bg-white"}>
          <div className="flex gap-5">
            <BackButton handleClick={() => navigate("/sg/users")} />
            <span className="text-[#757575] font-semibold">
              {t("common.back")}
            </span>
          </div>
          <div className="w-full mb-2 text-center">
            <h1
              className={
                isDarkMode
                  ? "text-2xl font-bold text-white w-full"
                  : "text-2xl font-bold text-primary-500 w-full"
              }
            >
              {isNew ? t("userForm.titleNew") : t("userForm.titleEdit")}
            </h1>
            <div
              className={
                isDarkMode
                  ? "w-full h-0.5 bg-dark-border mt-2 rounded-full"
                  : "w-full h-0.5 bg-[#DFE5EF] mt-2 rounded-full"
              }
            ></div>
          </div>

          <div
            className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${
              isDarkMode
                ? "border-amber-500/30 bg-amber-500/10 text-amber-200"
                : "border-amber-300 bg-amber-50 text-amber-800"
            }`}
          >
            <i className="ri-links-line mt-0.5 text-lg"></i>
            <div>
              <p className="text-sm font-semibold">
                {t("userForm.syncNotice.title")}
              </p>
              <p className="mt-0.5 text-xs">{t("userForm.syncNotice.text")}</p>
            </div>
          </div>

          <Section>
            <SectionTitle>
              <span className={sectionSpan}>{t("userForm.sections.details")}</span>
            </SectionTitle>
            <SectionContainer>
              <InputContainer>
                <InputLabel className={labelClass}>
                  {t("userForm.fields.userName")} *
                </InputLabel>
                <Input
                  className={inputClass}
                  placeholder={t("userForm.placeholders.userName")}
                  {...register("user_name", { required: true })}
                />
                {errors.user_name && (
                  <ErrorLabel>{t("common.required")}</ErrorLabel>
                )}
              </InputContainer>
              <InputContainer>
                <InputLabel className={labelClass}>
                  {t("userForm.fields.company")}
                </InputLabel>
                <Input
                  className={inputClass}
                  placeholder={t("userForm.placeholders.company")}
                  {...register("company")}
                />
              </InputContainer>
              <InputContainer>
                <InputLabel className={labelClass}>
                  {t("userForm.fields.firstName")}
                </InputLabel>
                <Input
                  className={inputClass}
                  placeholder={t("userForm.placeholders.firstName")}
                  {...register("first_name")}
                />
              </InputContainer>
              <InputContainer>
                <InputLabel className={labelClass}>
                  {t("userForm.fields.lastName")}
                </InputLabel>
                <Input
                  className={inputClass}
                  placeholder={t("userForm.placeholders.lastName")}
                  {...register("last_name")}
                />
              </InputContainer>
              <InputContainer>
                <InputLabel className={labelClass}>
                  {t("userForm.fields.origin")} *
                </InputLabel>
                <Controller
                  name="origin"
                  control={control}
                  rules={{ validate: (v: any) => (v && v.length > 0) || true }}
                  render={({ field }) => (
                    <Select
                      isMulti
                      options={ORIGIN_OPTIONS}
                      styles={getCustomSelectStyles(isDarkMode) as any}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      placeholder={t("userForm.placeholders.origin")}
                      value={ORIGIN_OPTIONS.filter((o) =>
                        (field.value as string[])?.includes(o.value)
                      )}
                      onChange={(opts: any) =>
                        field.onChange((opts || []).map((o: any) => o.value))
                      }
                    />
                  )}
                />
                {errors.origin && <ErrorLabel>{t("common.required")}</ErrorLabel>}
              </InputContainer>
              <InputContainer>
                <InputLabel className={labelClass}>
                  {t("userForm.fields.status")} *
                </InputLabel>
                <Controller
                  name="user_status_id"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select
                      options={statusOptions}
                      styles={getCustomSelectStyles(isDarkMode) as any}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      placeholder={t("userForm.placeholders.select")}
                      value={
                        statusOptions.find(
                          (o: any) => String(o.value) === String(field.value)
                        ) ?? null
                      }
                      onChange={(opt: any) => field.onChange(opt ? opt.value : "")}
                    />
                  )}
                />
                {errors.user_status_id && (
                  <ErrorLabel>{t("common.required")}</ErrorLabel>
                )}
              </InputContainer>
              <InputContainer>
                <InputLabel className={labelClass}>
                  {t("userForm.fields.tag")}
                </InputLabel>
                <Controller
                  name="account_condition_id"
                  control={control}
                  render={({ field }) => (
                    <Select
                      isClearable
                      options={conditionOptions}
                      styles={getCustomSelectStyles(isDarkMode) as any}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      placeholder={t("userForm.placeholders.tag")}
                      value={
                        conditionOptions.find(
                          (o: any) => String(o.value) === String(field.value)
                        ) ?? null
                      }
                      onChange={(opt: any) => field.onChange(opt ? opt.value : "")}
                    />
                  )}
                />
              </InputContainer>
              <InputContainer>
                <InputLabel className={labelClass}>
                  {t("userForm.fields.interval")} *
                </InputLabel>
                <Input
                  type="number"
                  className={inputClass}
                  placeholder={t("userForm.placeholders.interval")}
                  {...register("map_interval", { required: true })}
                />
                {errors.map_interval && (
                  <ErrorLabel>{t("common.required")}</ErrorLabel>
                )}
              </InputContainer>
              <div className="flex items-center gap-2 mt-2">
                <input
                  type="checkbox"
                  className="w-4 h-4 accent-primary-500"
                  {...register("not_reporting_alert")}
                />
                <label className={isDarkMode ? "text-white" : "text-gray-700"}>
                  {t("userForm.fields.notReporting")}
                </label>
              </div>
            </SectionContainer>
          </Section>

          {showV5Access && (
            <Section>
              <SectionTitle>
                <span className={sectionSpan}>{t("userForm.v5Access.title")}</span>
              </SectionTitle>
              <SectionContainer>
                <p className={`col-span-full text-sm ${labelClass}`}>
                  {t("userForm.v5Access.note")}
                </p>
                <InputContainer>
                  <InputLabel className={labelClass}>
                    {t("userForm.v5Access.email")} *
                  </InputLabel>
                  <Input
                    type="email"
                    className={inputClass}
                    placeholder="correo@dominio.com"
                    {...register("v5_email", { required: showV5Access })}
                  />
                  {(errors as any).v5_email && (
                    <ErrorLabel>{t("common.required")}</ErrorLabel>
                  )}
                </InputContainer>
                <InputContainer>
                  <InputLabel className={labelClass}>
                    {t("userForm.v5Access.username")} *
                  </InputLabel>
                  <Input
                    className={inputClass}
                    placeholder="usuario_v5"
                    {...register("v5_username", {
                      required: showV5Access,
                      pattern: /^[a-zA-Z0-9_]{3,30}$/,
                    })}
                  />
                  {(errors as any).v5_username && (
                    <ErrorLabel>{t("userForm.v5Access.usernameHint")}</ErrorLabel>
                  )}
                </InputContainer>
                <InputContainer>
                  <InputLabel className={labelClass}>
                    {t("userForm.v5Access.password")} *
                  </InputLabel>
                  <Input
                    type="password"
                    className={inputClass}
                    placeholder="mín. 8 caracteres"
                    autoComplete="new-password"
                    {...register("v5_password", {
                      required: showV5Access,
                      minLength: 8,
                    })}
                  />
                  {(errors as any).v5_password && (
                    <ErrorLabel>{t("userForm.v5Access.passwordHint")}</ErrorLabel>
                  )}
                </InputContainer>
              </SectionContainer>
            </Section>
          )}

          <Section>
            <SectionTitle>
              <span className={sectionSpan}>{t("userForm.sections.billing")}</span>
            </SectionTitle>
            <SectionContainer>
              {ADDRESS_FIELDS.map((f) => (
                <InputContainer
                  key={`billing-${f.name}`}
                  className={f.full ? "col-span-full" : ""}
                >
                  <InputLabel className={labelClass}>
                    {t(`userForm.fields.${f.labelKey}`)}
                  </InputLabel>
                  <Input
                    className={inputClass}
                    {...register(`billing.${f.name}` as any)}
                  />
                </InputContainer>
              ))}
              <InputContainer>
                <InputLabel className={labelClass}>
                  {t("userForm.fields.taxId")}
                </InputLabel>
                <Input className={inputClass} {...register("billing.tax_id" as any)} />
              </InputContainer>
            </SectionContainer>
          </Section>

          <Section>
            <SectionTitle>
              <span className={sectionSpan}>{t("userForm.sections.contact")}</span>
            </SectionTitle>
            <SectionContainer>
              {ADDRESS_FIELDS.map((f) => (
                <InputContainer
                  key={`contact-${f.name}`}
                  className={f.full ? "col-span-full" : ""}
                >
                  <InputLabel className={labelClass}>
                    {t(`userForm.fields.${f.labelKey}`)}
                  </InputLabel>
                  <Input
                    className={inputClass}
                    {...register(`contact.${f.name}` as any)}
                  />
                </InputContainer>
              ))}
            </SectionContainer>
          </Section>

          {!isNew && (
            <Section>
              <SectionTitle>
                <span className={sectionSpan}>
                  {t("userForm.v5Operation.title")}
                </span>
              </SectionTitle>
              <SectionContainer>
                <p className={`col-span-full text-sm ${labelClass}`}>
                  {t("userForm.v5Operation.note")}
                </p>
                {!linkedCompanyId && (
                  <p className={`col-span-full text-sm ${labelClass}`}>
                    {t("userForm.v5Operation.notLinked")}
                  </p>
                )}
                {linkedCompanyId && v5CompanyLoading && (
                  <p className={`col-span-full text-sm ${labelClass}`}>
                    {t("userForm.v5Operation.loading")}
                  </p>
                )}
                {linkedCompanyId && !v5CompanyLoading && !v5Company && (
                  <p className={`col-span-full text-sm ${labelClass}`}>
                    {t("userForm.v5Operation.unavailable")}
                  </p>
                )}
                {v5Company?.deleted && (
                  <p className={`col-span-full text-sm ${labelClass}`}>
                    {t("userForm.v5Operation.deletedInV5")}
                  </p>
                )}
                {v5Company && !v5Company.deleted && (
                <div className="col-span-full flex flex-wrap items-center gap-3">
                  <span className={`text-sm ${labelClass}`}>
                    {t("userForm.v5Operation.statusLabel")}
                  </span>
                  <span
                    className={`px-2 py-1 rounded text-xs font-medium ${
                      v5Company.suspended
                        ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                        : "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                    }`}
                  >
                    {v5Company.suspended
                      ? t("userForm.v5Operation.suspended")
                      : t("userForm.v5Operation.active")}
                  </span>
                  <Button
                    type="button"
                    className={
                      v5Company.suspended
                        ? "bg-green-600 hover:bg-green-500"
                        : "bg-amber-600 hover:bg-amber-500"
                    }
                    onClick={onToggleV5Operation}
                    disabled={
                      v5OperationMutation.isPending ||
                      (v5Company.suspended && savedStatusIsNotBillable)
                    }
                  >
                    {v5OperationMutation.isPending ? (
                      <span className="flex items-center gap-2">
                        <span className="inline-block w-3 h-3 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                        {v5Company.suspended
                          ? t("userForm.v5Operation.activating")
                          : t("userForm.v5Operation.suspending")}
                      </span>
                    ) : v5Company.suspended ? (
                      t("userForm.v5Operation.activate")
                    ) : (
                      t("userForm.v5Operation.suspend")
                    )}
                  </Button>
                </div>
                )}
                {v5Company?.suspended && savedStatusIsNotBillable && (
                  <p className={`col-span-full text-sm ${labelClass}`}>
                    {t("userForm.v5Operation.blockedByBilling")}
                  </p>
                )}
              </SectionContainer>
            </Section>
          )}

          {!isNew && (
            <Section>
              <SectionTitle>
                <span className={sectionSpan}>
                  {t("userForm.sections.devices")} ({associatedDevices.length})
                </span>
              </SectionTitle>
              {associatedDevices.length > 0 && (
                <div className="flex justify-end mb-2">
                  <button
                    type="button"
                    onClick={exportDevices}
                    className={`text-sm px-3 py-1.5 rounded-lg font-medium flex items-center gap-2 transition-colors border ${
                      isDarkMode
                        ? "border-dark-border text-gray-200 hover:bg-white/5"
                        : "border-gray-200 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <i className="ri-download-2-line"></i>
                    <span>{t("common.export")}</span>
                  </button>
                </div>
              )}
              <SgTable
                columns={deviceCols}
                rows={associatedDevices}
                isDarkMode={isDarkMode}
                emptyText={t("userForm.noDevices")}
                rowKey={(d) => String(d.id)}
                onRowClick={(d) => navigate(`/sg/devices/edit/${d.id}`)}
              />
            </Section>
          )}

          {!isNew && userData && (
            <Section>
              <AccountContacts
                key={id}
                userId={id!}
                initial={userData.accountContacts ?? []}
                contactTypes={lookups?.contactTypes ?? []}
                isDarkMode={isDarkMode}
              />
            </Section>
          )}
        </SectionsContainer>

        <ButtonsContainer>
          {!isNew && (
            <Button
              type="button"
              className="bg-red-600 hover:bg-red-500"
              onClick={onDelete}
              disabled={deleting || saving}
            >
              {deleting ? t("userForm.deleting") : t("common.delete")}
            </Button>
          )}
          <Button
            type="button"
            className="bg-dark-border hover:bg-[#444444]"
            onClick={() => navigate("/sg/users")}
          >
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={saving || deleting}>
            {saving ? t("common.saving") : t("common.save")}
          </Button>
        </ButtonsContainer>
      </Form>
    </FormContainer>
  );
}
